import useSalesProfitController from "@/hooks/useSalesProfitController";
import { Dialog, DialogContent } from "@/shared";
import HighlightText from "@/shared/components/HighlightText";
import DatePicker from "@/shared/components/ui/DatePicker";
import { Dropdown, DropdownItem } from "@/shared/components/ui/Dropdown";
import { AnimatePresence, motion } from 'framer-motion';
import {
    DollarSign, Layers, Loader, Search,
    Sparkle,
    TrendingUp,
    Wallet, ArrowDownWideNarrow, ArrowUpNarrowWide,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ReactPaginate from "react-paginate";
import { Link } from "react-router-dom";

export default function SalesProfits() {
    const { t } = useTranslation();
    const {
        salesProfits, isLoading, handleQuickSelect, date, setDate,
        salesProfitsQuery, projectsWithBusiness, selectedProject, setSelectedProject,
        askCancelOrder, confirmCancelOrder, dismissDialog, confirmDialog, isCancelling,
        searchValue, setSearchValue,
        page, setPage, totalPages, pageSize, totalOrders, handleExport,
        isSearching, totalProfitAmount, totalExpensesAmount, summary, isSummaryLoading,
        itemsSoldRanked, isItemsRankedLoading,
        netProfitAmount, sortOrder, setSortOrder
    } = useSalesProfitController();


    const [expandedCard, setExpandedCard] = useState<string | null>(null);

    const formatNumber = (value: any) => {
        const n = Number(value);
        if (Number.isNaN(n)) return '—';
        return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
    };

    const statCards = [
        {
            key: 'best_selling_item',
            label: t('best_selling_item'),
            value: summary?.best_selling_item ?? '—',
            Icon: Sparkle,
            isText: true,
            details: `${t('sold_quantity')}: ${formatNumber(summary?.best_selling_qty)} ${t('pieces', 'قطعة')}`,
        },
        // {
        //     key: 'least_selling_item',
        //     label: t('least_selling_item'),
        //     value: summary?.least_selling_item ?? '—',
        //     Icon: TrendingDown,
        //     isText: true,
        //     details: `${t('sold_quantity')}: ${formatNumber(summary?.least_selling_qty)} ${t('pieces', 'قطعة')}`,
        // },
        {
            key: 'total_pieces',
            label: t('total_pieces'),
            value: formatNumber(summary?.total_pieces),
            Icon: Layers,
            details: t('total_pieces_details'),
        },
        {
            key: 'total_cost',
            label: t('total_cost'),
            value: formatNumber(summary?.total_cost),
            Icon: Wallet,
            details: t('total_cost_details'),
        },
        {
            key: 'total_sales',
            label: t('total_sales'),
            value: formatNumber(summary?.total_sales),
            Icon: DollarSign,
            details: t('total_sales_details'),
        },
        {
            key: 'total_profit',
            label: t('total_profit'),
            value: formatNumber(totalProfitAmount),
            Icon: TrendingUp,
            details: t('total_profit_details'),
        },
        {
            key: 'total_expenses',
            label: t('expenses'),
            value: formatNumber(totalExpensesAmount),
            Icon: Wallet,
            details: t('total_expenses_details', 'اضغط لعرض تفاصيل المصروفات'),
            href: '/expenses',
        },
        {
            key: 'net_profit',
            label: t('net_profit', 'صافي الربح'),
            value: formatNumber(netProfitAmount),
            Icon: TrendingUp,
            details: t('net_profit_details', 'إجمالي الربح بعد خصم المصروفات'),
        },
    ];

    const activeCard = statCards.find((c) => c.key === expandedCard);

    return (
        <>
            {/* <Header title={t("sales_profits")} info={salesProfitsQuery} /> */}


            {/* <main className="px-4 py-8 min-h-[calc(100vh-100px)] flex flex-col items-center justify-start"> */}

            <section className="w-full  mb-6 bg-white p-4 md:p-6 rounded-2xl border border-slate-200 shadow-sm">
                <h2 className="text-sm font-bold text-slate-500 mb-4 text-center uppercase tracking-wide">
                    {t('sales_overview')}
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                    {statCards.map(({ key, label, value, Icon, isText, href }) => {
                        const CardInner = (
                            <>
                                <div className="flex items-center justify-between">
                                    <span className="text-[11px] font-bold uppercase tracking-wide text-gray-400">
                                        {label}
                                    </span>
                                    <Icon className="w-4 h-4 text-violet-500 shrink-0" />
                                </div>
                                <span
                                    className={
                                        key === 'net_profit'
                                            ? `text-xl font-extrabold ${netProfitAmount >= 0 ? 'text-emerald-600' : 'text-red-500'}`
                                            : (isText ? 'text-sm font-bold text-gray-800 truncate' : 'text-xl font-extrabold text-gray-800')
                                    }
                                    title={isText ? String(value) : undefined}
                                >
                                    {isSummaryLoading ? '…' : value}
                                </span>
                            </>
                        );

                        const cardClassName = `flex flex-col gap-2 rounded-2xl p-4 border text-right transition-all w-full ${expandedCard === key
                            ? 'bg-violet-50 border-violet-300 ring-2 ring-violet-200'
                            : 'bg-gray-50 border-gray-100 hover:border-violet-200'
                            }`;

                        return href ? (
                            <Link key={key} to={href} title={label} className={cardClassName}>
                                {CardInner}
                            </Link>
                        ) : (
                            <button
                                key={key}
                                type="button"
                                onClick={() => setExpandedCard((prev) => (prev === key ? null : key))}
                                className={cardClassName}
                            >
                                {CardInner}
                            </button>
                        );
                    })}
                </div>

                <AnimatePresence mode="wait">
                    {activeCard && (
                        <motion.div
                            key={activeCard.key}
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                        >
                            <div className="mt-4 bg-violet-50 border border-violet-100 rounded-2xl p-4">
                                {(activeCard.key === 'best_selling_item' || activeCard.key === 'least_selling_item') ? (
                                    <div>
                                        <div className="flex items-center gap-3 mb-3">
                                            <activeCard.Icon className="w-5 h-5 text-violet-500 shrink-0" />
                                            <div className="text-sm font-bold text-slate-800">
                                                {activeCard.key === 'best_selling_item'
                                                    ? t('items_ranked_best_to_least', 'كل الموديلات: من الأكثر إلى الأقل مبيعاً')
                                                    : t('items_ranked_least_to_best', 'كل الموديلات: من الأقل إلى الأكثر مبيعاً')}
                                            </div>
                                        </div>

                                        {isItemsRankedLoading ? (
                                            <div className="text-xs text-slate-500 py-4 text-center">{t('loading')}</div>
                                        ) : itemsSoldRanked.length === 0 ? (
                                            <div className="text-xs text-slate-500 py-4 text-center">{t('no_available_data')}</div>
                                        ) : (
                                            <div className="max-h-72 overflow-y-auto rounded-xl border border-violet-100 divide-y divide-violet-100 bg-white">
                                                {(activeCard.key === 'least_selling_item'
                                                    ? [...itemsSoldRanked].reverse()
                                                    : itemsSoldRanked
                                                ).map((item: any, idx: number) => (
                                                    <div key={idx} className="flex items-center justify-between px-3 py-2 text-xs">
                                                        <span className="text-slate-700 font-medium truncate ml-2">
                                                            {idx + 1}. {item.item_name}
                                                        </span>
                                                        <span className="text-violet-600 font-bold shrink-0">
                                                            {formatNumber(item.total_qty)} {t('pieces', 'قطعة')}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-3">
                                        <activeCard.Icon className="w-5 h-5 text-violet-500 shrink-0" />
                                        <div>
                                            <div className="text-sm font-bold text-slate-800">{activeCard.label}: {activeCard.value}</div>
                                            <div className="text-xs text-slate-600 mt-0.5">{activeCard.details}</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </section>

            <div className="w-full max-w-7xl">
                <div className="mb-6 w-full max-w-7xl grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 items-end bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
                    <div className="flex flex-col gap-1.5 w-full">
                        <div className="flex items-center justify-between">
                            <label className="text-xs font-semibold text-slate-500">{t("start_date")}</label>
                            <Dropdown label={t("quick_select")}>
                                <DropdownItem onClick={() => handleQuickSelect('today')}>{t("today")}</DropdownItem>
                                <DropdownItem onClick={() => handleQuickSelect('yesterday')}>{t("yesterday")}</DropdownItem>
                                <DropdownItem onClick={() => handleQuickSelect('current_month')}>{t("current month")}</DropdownItem>
                                <DropdownItem onClick={() => handleQuickSelect('last_month')}>{t("before month")}</DropdownItem>
                            </Dropdown>
                        </div>
                        <DatePicker id="pp-from" label="" placeholder={t("Select a date")}
                            onChange={(_, d) => setDate(p => ({ ...p, from: d }))} defaultDate={date.from} />
                    </div>

                    <div className="flex flex-col gap-1.5 w-full">
                        <div className="flex items-center justify-between">
                            <label className="text-xs font-semibold text-slate-500">{t("end_date")}</label>
                            <Dropdown label={t("quick_select")}>
                                <DropdownItem onClick={() => handleQuickSelect('today')}>{t("today")}</DropdownItem>
                                <DropdownItem onClick={() => handleQuickSelect('yesterday')}>{t("yesterday")}</DropdownItem>
                                <DropdownItem onClick={() => handleQuickSelect('current_month')}>{t("current month")}</DropdownItem>
                                <DropdownItem onClick={() => handleQuickSelect('last_month')}>{t("before month")}</DropdownItem>
                            </Dropdown>
                        </div>
                        <DatePicker id="pp-to" label="" placeholder={t("Select a date")}
                            onChange={(_, d) => setDate(p => ({ ...p, to: d }))} defaultDate={date.to} />
                    </div>

                    <div className="flex flex-col gap-1.5 w-full">
                        <label className="text-xs font-semibold text-slate-500">{t("Project")}</label>
                        <div className="relative w-full">
                            <select
                                name="project"
                                value={selectedProject}
                                onChange={(e) => setSelectedProject(e.target.value)}
                                className="w-full appearance-none bg-white border border-slate-200 rounded-xl px-4 py-2.5 pr-10 text-sm text-slate-900 font-medium shadow-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-400 outline-none cursor-pointer transition-all hover:border-slate-300"
                            >
                                <option value="">{t("Select Project")}</option>
                                {projectsWithBusiness?.map((project: any, index: number) => (
                                    <option key={index} value={project?.business_id}>
                                        {project.type_name}
                                    </option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </div>
                    </div>
   <div className="flex flex-col gap-1.5 w-full">
                        <label className="text-xs font-semibold text-slate-500">{t("ترتيب حسب التاريخ")}</label>
                        <button
                            type="button"
                            onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                            className="w-full flex items-center justify-center gap-2 h-[42px] px-4 rounded-xl border text-sm font-medium bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 shadow-sm transition-all duration-200 active:scale-[0.98] cursor-pointer"
                        >
                            {sortOrder === 'asc' ? (
                                <>
                                    <ArrowUpNarrowWide className="w-4 h-4 text-violet-600" />
                                    <span>{t("الأقدم للأحدث")}</span>
                                </>
                            ) : (
                                <>
                                    <ArrowDownWideNarrow className="w-4 h-4 text-violet-600" />
                                    <span>{t("الأحدث للأقدم")}</span>
                                </>
                            )}
                        </button>
                    </div>
                    <div className="flex flex-col gap-1.5 w-full lg:col-span-2">
                        <label className="text-xs font-semibold text-slate-500">{t("search")}</label>
                        <div className="flex items-center gap-2">
                            <div className="relative flex-1">
                                <input
                                    value={searchValue}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                    type="text"
                                    placeholder={t("search_by_name_phone_or_id")}
                                    className="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 shadow-sm transition-all hover:border-slate-300"
                                />
                                <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
                                    {isSearching ? (
                                        <Loader className="w-4 h-4 text-emerald-500 animate-spin" />
                                    ) : (
                                        <Search className="w-4 h-4 text-slate-400" />
                                    )}
                                </div>
                            </div>

                            <button
                                onClick={() => handleExport('pdf', 'تقرير مبيعات الأصناف')}
                                className="shrink-0 bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold px-3 py-2.5 rounded-xl shadow-sm transition-colors"
                            >
                                تصدير PDF
                            </button>
                            <button
                                onClick={() => handleExport('excel', 'تقرير مبيعات الأصناف')}
                                className="shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-2.5 rounded-xl shadow-sm transition-colors"
                            >
                                تصدير Excel
                            </button>
                        </div>
                    </div>
                 
                </div>

                <div className="bg-white rounded-[1rem] border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden relative">
                    {isLoading ? (
                        <div className="py-20 flex items-center justify-center">
                            <Loader />
                        </div>
                    ) : (
                        <div className={`overflow-x-auto transition-opacity duration-200 ${isSearching ? 'opacity-50' : 'opacity-100'}`}>
                            <table className="w-full text-right border-collapse">
                                <thead>
                                    <tr className="text-slate-900 uppercase font-black tracking-widest border-b border-slate-100 bg-slate-50/30">
                                        <th className="px-6 py-4">{t("order_id")}</th>
                                        <th className="px-6 py-4">{t("account_name")}</th>
                                        <th className="px-6 py-4">{t("order_date")}</th>
                                        <th className="px-6 py-4">{t("item_name")}</th>
                                        <th className="px-6 py-4">{t("order_quantity")}</th>
                                        <th className="px-6 py-4">{t("sell_price")}</th>
                                        <th className="px-6 py-4">{t("cost_price")}</th>
                                        <th className="px-6 py-4">{t("pcs_profit")}</th>
                                        <th className="px-6 py-4">{t("total_profit")}</th>
                                        <th className="px-6 py-4">{t("actions")}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {salesProfits.length === 0 ? (
                                        <tr>
                                            <td
                                                colSpan={10}
                                                className="px-6 py-12 text-center text-slate-900 text-sm italic"
                                            >
                                                {t("no_available_data")}
                                            </td>
                                        </tr>
                                    ) : (
                                        salesProfits?.map((row: any, index: number) => (
                                            <motion.tr
                                                key={index}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                className="hover:bg-slate-50/50 transition-colors"
                                            >
                                                <td className="px-6 py-4 text-sm font-medium text-violet-600 hover:underline">
                                                    <Link to={`/order_tracking/${row.order_id}`}>
                                                        #<HighlightText search={searchValue} text={row?.order_id || "---"} />
                                                    </Link>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-slate-900">
                                                    <HighlightText search={searchValue} text={row?.accoutn_name || "---"} />
                                                </td>
                                                <td className="px-6 py-4 text-sm text-slate-900">{row?.order_date || "---"}</td>
                                                <td className="px-6 py-4 text-sm text-slate-900">{row?.item_name || "---"}</td>
                                                <td className="px-6 py-4 text-sm text-slate-900">{Number(row?.order_quantity || 0).toLocaleString()}</td>
                                                <td className="px-6 py-4 text-sm font-semibold text-violet-600">{Number(row?.sell_price || 0).toLocaleString()}</td>
                                                <td className="px-6 py-4 text-sm font-semibold text-red-500">{Number(row?.cost_price || 0).toLocaleString()}</td>
                                                <td className={`px-6 py-4 text-sm font-bold ${Number(row?.pcs_profit) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                                    {Number(row?.pcs_profit || 0).toLocaleString()}
                                                </td>
                                                <td className={`px-6 py-4 text-sm font-bold ${Number(row?.total_profit) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                                    {Number(row?.total_profit || 0).toLocaleString()}
                                                </td>
                                                <td className="px-6 py-4">
                                                    {row?.manualid === 'cancelled' ? (
                                                        <span className="text-xs bg-slate-100 text-slate-900 px-3 py-1.5 rounded-lg font-semibold">
                                                            {t("cancelled")}
                                                        </span>
                                                    ) : (
                                                        <button
                                                            onClick={() => askCancelOrder(row.order_id)}
                                                            className="text-xs bg-red-50 text-red-500 hover:bg-red-100 px-3 py-1.5 rounded-lg font-semibold transition-colors"
                                                        >
                                                            {t("cancel")}
                                                        </button>
                                                    )}
                                                </td>
                                            </motion.tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between px-6 py-3 border-t border-slate-100 bg-slate-50/30">
                            <span className="text-xs text-slate-500">
                                {page * pageSize + 1}–{Math.min((page + 1) * pageSize, totalOrders)} {t("of")} {totalOrders}
                            </span>
                            <ReactPaginate
                                pageCount={totalPages}
                                forcePage={page}
                                onPageChange={({ selected }) => setPage(selected)}
                                marginPagesDisplayed={1}
                                pageRangeDisplayed={3}
                                previousLabel="‹"
                                nextLabel="›"
                                breakLabel="…"
                                containerClassName="flex items-center gap-1"
                                pageClassName="inline-flex cursor-pointer"
                                pageLinkClassName="min-w-[2rem] h-8 px-2 flex items-center justify-center rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-200 transition-colors"
                                activeClassName="!text-white"
                                activeLinkClassName="!bg-violet-600 !text-white shadow-sm"
                                previousClassName="inline-flex"
                                previousLinkClassName="cursor-pointer min-w-[2rem] h-8 px-2 flex items-center justify-center rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-200 transition-colors"
                                nextClassName="inline-flex"
                                nextLinkClassName="cursor-pointer min-w-[2rem] h-8 px-2 flex items-center justify-center rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-200 transition-colors"
                                breakClassName="inline-flex"
                                breakLinkClassName="min-w-[2rem] h-8 px-2 flex items-center justify-center text-slate-400 text-sm select-none"
                                disabledClassName="opacity-30 cursor-not-allowed pointer-events-none"
                                renderOnZeroPageCount={null}
                            />
                        </div>
                    )}
                </div>
            </div>
            {/* </main> */}

            <Dialog open={confirmDialog.open} onOpenChange={dismissDialog}>
                <DialogContent>
                    <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mx-auto mb-4">
                        <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-bold text-slate-800 text-center mb-1">{t("confirm_cancel")}</h3>
                    <p className="text-sm text-slate-900 text-center mb-6">
                        {t("confirm_cancel_message")} <span className="font-bold text-slate-900">#{confirmDialog.orderId}</span>؟
                    </p>
                    <div className="flex gap-3">
                        <button
                            onClick={dismissDialog}
                            disabled={isCancelling}
                            className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-900 text-sm font-semibold hover:bg-slate-50 transition-colors"
                        >
                            {t("no")}
                        </button>
                        <button
                            onClick={confirmCancelOrder}
                            disabled={isCancelling}
                            className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
                        >
                            {isCancelling ? (
                                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                </svg>
                            ) : t("yes_cancel")}
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}